// Máscaras e validação de documentos, telefone e e-mail.
//
// Módulo único: toda tela que pede CPF, CNPJ, telefone ou e-mail usa daqui.
// Máscara é apresentação — o que vai para o banco é o valor limpo, e a
// validação de verdade acontece no servidor.

export const apenasDigitos = (valor: string): string => valor.replace(/\D/g, '');

/**
 * Limite padrão de um campo de texto de uma linha.
 *
 * 255 é o teto adotado no produto: cabe em `varchar(255)` de qualquer
 * integração futura e impede que um campo de nome receba um texto de página
 * inteira. Campos que precisam de mais declaram o próprio limite.
 */
export const LIMITE_TEXTO = 255;

/** Limite padrão de um campo de texto longo. */
export const LIMITE_TEXTO_LONGO = 2000;

// ─── Máscaras ────────────────────────────────────────────────────────
// Todas aplicam progressivamente: funcionam enquanto a pessoa digita, sem
// esperar o campo estar completo.

/** XXX.XXX.XXX-XX */
export function mascaraCpf(valor: string): string {
  const d = apenasDigitos(valor).slice(0, 11);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `${d.slice(0, 3)}.${d.slice(3)}`;
  if (d.length <= 9) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`;
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
}

/**
 * Caracteres de um CNPJ, em maiúsculas e sem pontuação.
 *
 * Desde julho de 2026 o CNPJ é alfanumérico (IN RFB 2.229/2024): as doze
 * primeiras posições aceitam letra ou dígito, e só os dois verificadores
 * continuam numéricos. Os CNPJ numéricos já emitidos seguem válidos — este
 * normalizador atende os dois formatos.
 */
export function normalizarCnpj(valor: string): string {
  const bruto = valor
    .toUpperCase()
    .replace(/[^0-9A-Z]/g, '')
    .slice(0, 14);
  if (bruto.length <= 12) return bruto;
  return bruto.slice(0, 12) + apenasDigitos(bruto.slice(12));
}

/** XX.XXX.XXX/XXXX-XX */
export function mascaraCnpj(valor: string): string {
  const d = normalizarCnpj(valor);
  if (d.length <= 2) return d;
  if (d.length <= 5) return `${d.slice(0, 2)}.${d.slice(2)}`;
  if (d.length <= 8) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5)}`;
  if (d.length <= 12) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8)}`;
  return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
}

/** (XX) XXXXX-XXXX para celular; (XX) XXXX-XXXX para fixo. */
export function mascaraTelefone(valor: string): string {
  const d = apenasDigitos(valor).slice(0, 11);
  if (d.length <= 2) return d.length ? `(${d}` : '';
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
}

/** XXXXX-XXX */
export function mascaraCep(valor: string): string {
  const d = apenasDigitos(valor).slice(0, 8);
  return d.length > 5 ? `${d.slice(0, 5)}-${d.slice(5)}` : d;
}

export function mascaraDocumento(valor: string, tipo: 'pf' | 'pj'): string {
  return tipo === 'pj' ? mascaraCnpj(valor) : mascaraCpf(valor);
}

// ─── Validação ───────────────────────────────────────────────────────

/**
 * CPF com dígitos verificadores.
 *
 * Conferir só o comprimento aceitaria `111.111.111-11` e qualquer erro de
 * digitação — o dígito verificador existe justamente para isso.
 */
export function validarCpf(valor: string): boolean {
  const d = apenasDigitos(valor);
  if (d.length !== 11) return false;
  if (/^(\d)\1{10}$/.test(d)) return false;

  const digito = (ate: number): number => {
    let soma = 0;
    let peso = ate + 1;
    for (let i = 0; i < ate; i += 1) {
      soma += Number(d[i]) * peso;
      peso -= 1;
    }
    const resto = (soma * 10) % 11;
    return resto === 10 ? 0 : resto;
  };

  return digito(9) === Number(d[9]) && digito(10) === Number(d[10]);
}

/**
 * CNPJ numérico ou alfanumérico, com dígitos verificadores.
 *
 * O cálculo é o de sempre; muda só o valor de cada caractere, que passou a ser
 * o código ASCII menos 48 — `'0'` vale 0, `'9'` vale 9 e `'A'` vale 17. Para um
 * CNPJ só de dígitos o resultado é idêntico ao da regra anterior.
 */
export function validarCnpj(valor: string): boolean {
  const c = normalizarCnpj(valor);
  if (!/^[0-9A-Z]{12}\d{2}$/.test(c)) return false;
  if (/^(.)\1{13}$/.test(c)) return false;

  const digito = (ate: number): number => {
    const pesos =
      ate === 12 ? [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2] : [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
    let soma = 0;
    for (let i = 0; i < ate; i += 1) soma += (c.charCodeAt(i) - 48) * pesos[i];
    const resto = soma % 11;
    return resto < 2 ? 0 : 11 - resto;
  };

  return digito(12) === Number(c[12]) && digito(13) === Number(c[13]);
}

export function validarDocumento(valor: string, tipo: 'pf' | 'pj'): boolean {
  return tipo === 'pj' ? validarCnpj(valor) : validarCpf(valor);
}

/**
 * Documento pronto para gravar: sem pontuação e sem perder a letra de um CNPJ
 * alfanumérico — `apenasDigitos` aqui apagaria metade do documento.
 */
export function limparDocumento(valor: string, tipo: 'pf' | 'pj'): string {
  return tipo === 'pj' ? normalizarCnpj(valor) : apenasDigitos(valor).slice(0, 11);
}

/**
 * RG.
 *
 * Não existe formato nacional: cada estado emite o seu, com 7 a 14 caracteres,
 * pontuação própria e dígito verificador que pode ser um número ou X. Impor a
 * máscara de São Paulo (`12.345.678-9`) corromperia o documento de quem é de
 * outro estado.
 *
 * Então aqui não se formata: apenas se limpa. Ficam dígitos e o X final, em
 * maiúscula. É o suficiente para o dado entrar comparável no banco sem afirmar
 * um formato que não existe.
 */
export function mascaraRg(valor: string): string {
  const limpo = valor
    .toUpperCase()
    .replace(/[^0-9X]/g, '')
    .slice(0, 14);
  // X só faz sentido como dígito verificador, no fim.
  return limpo.replace(/X(?=.)/g, '');
}

/** RG plausível: 5 a 14 caracteres, com X somente na última posição. */
export function validarRg(valor: string): boolean {
  const limpo = mascaraRg(valor);
  return /^[0-9]{4,13}X?$/.test(limpo) && limpo.length >= 5;
}

/**
 * Telefone brasileiro: DDD válido e 10 (fixo) ou 11 (celular) dígitos.
 * Celular precisa começar com 9 depois do DDD.
 */
export function validarTelefone(valor: string): boolean {
  const d = apenasDigitos(valor);
  if (d.length !== 10 && d.length !== 11) return false;

  const ddd = Number(d.slice(0, 2));
  if (ddd < 11 || ddd > 99) return false;

  if (d.length === 11 && d[2] !== '9') return false;
  return true;
}

/**
 * E-mail.
 *
 * Deliberadamente simples: exige parte local, arroba, domínio e um TLD com ao
 * menos duas letras. Regex de e-mail "completa" rejeita endereços válidos e
 * ainda assim não prova que a caixa existe — quem prova isso é o envio.
 */
export function validarEmail(valor: string): boolean {
  const limpo = valor.trim();
  if (!limpo || limpo.length > 254) return false;
  if (/\s/.test(limpo)) return false;
  return /^[^@]+@[^@.]+(\.[^@.]+)*\.[a-zA-Z]{2,}$/.test(limpo);
}

// ─── Mensagens ───────────────────────────────────────────────────────
// Uma frase por erro, usada igual em todas as telas.

export const MENSAGEM = {
  cpf: 'CPF inválido.',
  cnpj: 'CNPJ inválido.',
  rg: 'RG inválido. Informe só números (e X, se houver).',
  telefone: 'Telefone inválido. Use DDD + número.',
  email: 'E-mail inválido.',
  cep: 'CEP deve ter 8 dígitos.',
} as const;
